// engine-exception: the public booking page is what a customer opens from an email — no account, no shell, no engine client auth; a token capability page like the public quote
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, createClient, type Client } from '../../engine/api/client';

// "Pick a time" — the page that ends phone tag (docs/servicedesk-v2-proposal.md P3). It talks to
// /public/booking/:token, which needs no auth: the token in the URL is the capability. Deliberately
// self-contained — no shell, no sidebar, no engine client — because the person opening it is a
// customer on a phone, not a technician.

interface Slot { start: string; end: string; technician_id: string; technician_name: string }
interface Invitation {
  status: 'open' | 'booked' | 'expired' | 'cancelled';
  expires_at: string;
  type: { name: string; description: string | null; duration_minutes: number; location: string };
  ticket: { ref: string | null; summary: string | null } | null;
  client_name: string | null;
  contact_name: string | null;
  appointment: { starts_at: string; ends_at: string; technician_name: string | null } | null;
  slots: Slot[];
}

const LOCATION_LABEL: Record<string, string> = { remote: 'Remote session', onsite: 'We come to you', phone: 'We call you' };

const dayLabel = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** The public client: no bearer token, same base URL as the app's API client. */
export function publicClient(): Client {
  return createClient(async () => null, {});
}

function messageOf(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail ?? e.message ?? fallback;
  return e instanceof Error ? e.message : fallback;
}

export function BookingPage({ client }: { client?: Client } = {}) {
  const { token = '' } = useParams();
  const api = useMemo(() => client ?? publicClient(), [client]);
  const [state, setState] = useState<{ loading: boolean; error: string | null; data: Invitation | null }>({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await api.request<Invitation>(`/public/booking/${encodeURIComponent(token)}`);
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: messageOf(e, 'This link is not valid'), data: null });
    }
  }, [api, token]);

  useEffect(() => {
    void load();
    // Best effort: the desk wants to know an invitation was opened but never used.
    void api.request(`/public/booking/${encodeURIComponent(token)}/view`, { method: 'POST', body: {} }).catch(() => undefined);
  }, [api, load, token]);

  const byDay = useMemo(() => {
    const days = new Map<string, Slot[]>();
    for (const s of state.data?.slots ?? []) {
      const key = s.start.slice(0, 10);
      if (!days.has(key)) days.set(key, []);
      days.get(key)!.push(s);
    }
    return [...days.entries()].slice(0, 10);
  }, [state.data]);

  const choose = async (slot: Slot) => {
    setBusy(slot.start);
    try {
      await api.request(`/public/booking/${encodeURIComponent(token)}/book`, { method: 'POST', body: { start: slot.start } });
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: messageOf(e, 'That time is no longer free') }));
      await load();
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) return <main className="hf-public"><p>Loading…</p></main>;
  if (!state.data) return <main className="hf-public"><h1>This link is not valid</h1><p>{state.error}</p></main>;

  const inv = state.data;
  if (inv.appointment) {
    return (
      <main className="hf-public">
        <h1>You are booked in</h1>
        <p className="hf-lead">
          {dayLabel(inv.appointment.starts_at)} at {timeLabel(inv.appointment.starts_at)}
          {inv.appointment.technician_name ? ` with ${inv.appointment.technician_name}` : ''}.
        </p>
        <p>{LOCATION_LABEL[inv.type.location] ?? inv.type.name} · {inv.type.duration_minutes} minutes</p>
        {inv.ticket?.ref && <p className="hf-muted">Your reference: {inv.ticket.ref}</p>}
        <p className="hf-muted">Need to change it? Reply to the email and we will sort it out.</p>
      </main>
    );
  }
  if (inv.status !== 'open') {
    return (
      <main className="hf-public">
        <h1>This link has {inv.status === 'expired' ? 'expired' : 'been withdrawn'}</h1>
        <p>Reply to our email and we will send you a new one.</p>
      </main>
    );
  }

  return (
    <main className="hf-public">
      <h1>Pick a time</h1>
      <p className="hf-lead">
        {inv.type.name} · {inv.type.duration_minutes} minutes · {LOCATION_LABEL[inv.type.location] ?? inv.type.location}
      </p>
      {inv.type.description && <p>{inv.type.description}</p>}
      {inv.ticket?.summary && <p className="hf-muted">About: {inv.ticket.summary} ({inv.ticket.ref})</p>}
      {state.error && <p role="alert" className="hf-error">{state.error}</p>}

      {byDay.length === 0 ? (
        <p>There is nothing free at the moment — reply to our email and we will find you a time.</p>
      ) : (
        byDay.map(([day, slots]) => (
          <section key={day}>
            <h2>{dayLabel(day)}</h2>
            <div className="hf-slotgrid">
              {slots.map((s) => (
                <button key={s.start} type="button" className="hf-slot" disabled={busy !== null} onClick={() => void choose(s)}>
                  {busy === s.start ? '…' : timeLabel(s.start)}
                </button>
              ))}
            </div>
          </section>
        ))
      )}
      <p className="hf-muted">All times are shown in your own timezone.</p>
    </main>
  );
}
